import type { ActionPlanWithOwner } from '@/types'
import { ActionPlanStatusBadge } from './action-plan-status-badge'
import { ReflectionsSection } from './reflections-section'
import { calendarDateOf, formatCalendarDate } from '../../functions-src/lib/calendar'
import { canManagePlan, type ActionPlanRole } from '../../functions-src/lib/action-plan-access'

interface ActionPlanCardProps {
  plan: ActionPlanWithOwner
  role?: ActionPlanRole
  goalOwnerId?: string | null
  currentUserId?: string
  onEdit: () => void
  onComplete: () => void
  onDelete: () => void
  onArchive?: () => void
  onUnarchive?: () => void
}

function formatDate(timestamp: number | undefined): string {
  if (!timestamp) return '—'
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function ActionPlanCard({ plan, role, goalOwnerId, currentUserId, onEdit, onComplete, onDelete, onArchive, onUnarchive }: ActionPlanCardProps) {
  const isArchived = !!plan.archived_at
  const isCompletable = plan.status === 'active' && !isArchived
  const isEditable = plan.status !== 'completed' && plan.status !== 'expired' && !isArchived
  const isArchivable = (plan.status === 'completed' || plan.status === 'expired') && !isArchived
  // Plan actions restricted to admin or the current goal owner
  const canModify = canManagePlan(role, currentUserId, goalOwnerId)

  return (
    <div className="bg-white rounded-lg border border-cfa-gray p-4 hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-cfa-dark-gray truncate">{plan.title}</h4>
          <p className="text-sm text-cfa-dark-gray/70 mt-0.5">
            Owner: {plan.owner_name}
          </p>
        </div>
        <ActionPlanStatusBadge status={plan.status} />
      </div>

      {/* Description */}
      {plan.description && (
        <p className="text-sm text-cfa-dark-gray mt-3 line-clamp-2">{plan.description}</p>
      )}

      {/* Expected Outcome */}
      {plan.expected_outcome && (
        <div className="mt-3 text-sm">
          <span className="font-medium text-cfa-dark-gray">Expected outcome: </span>
          <span className="text-cfa-dark-gray/80">{plan.expected_outcome}</span>
        </div>
      )}

      {/* Dates */}
      <div className="flex items-center gap-4 mt-3 text-xs text-cfa-dark-gray/70">
        {plan.start_date && (
          <span>Start: {formatCalendarDate(calendarDateOf(plan.start_date))}</span>
        )}
        {plan.target_date && (
          <span>Target: {formatCalendarDate(calendarDateOf(plan.target_date))}</span>
        )}
        {plan.completed_at && (
          <span className="text-success-600">Completed: {formatDate(plan.completed_at)}</span>
        )}
      </div>

      {/* Actions (admin or goal owner) */}
      {canModify && (
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-cfa-light-gray">
          {isArchived ? (
            <button
              onClick={onUnarchive}
              className="text-xs text-brand-blue hover:text-brand-blue/80 font-medium"
            >
              Unarchive
            </button>
          ) : (
            <>
              {isCompletable && (
                <button
                  onClick={onComplete}
                  className="text-xs text-success-600 hover:text-success-700 font-medium"
                >
                  Mark Complete
                </button>
              )}
              {isEditable && (
                <button
                  onClick={onEdit}
                  className="text-xs text-brand-blue hover:text-brand-blue/80 font-medium"
                >
                  Edit
                </button>
              )}
              {isArchivable && (
                <button
                  onClick={onArchive}
                  className="text-xs text-cfa-dark-gray/60 hover:text-cfa-dark-gray font-medium"
                >
                  Archive
                </button>
              )}
              <button
                onClick={onDelete}
                className="text-xs text-danger-500 hover:text-danger-200 font-medium"
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Reflections (completed/expired/archived plans) */}
      <ReflectionsSection
        planId={plan.id}
        planStatus={plan.status}
        isAdmin={role === 'admin'}
        currentUserId={currentUserId}
        archivedAt={plan.archived_at}
      />
    </div>
  )
}
